import React from 'react';
import { AppSettings } from '../state/types';
import { Editor } from './Editor';
import { Play, Sliders, Mic, FileText, Minus, Plus } from 'lucide-react';

interface HomeViewProps {
  scriptText: string;
  settings: AppSettings;
  canUndoClear: boolean;
  onScriptChange: (newScript: string) => void;
  onClearScript: () => void;
  onUndoClearScript: () => void;
  onUpdateSettings: (newSettings: Partial<AppSettings>) => void;
  onOpenSettings: () => void;
  onStartPrompter: () => void;
}

export const HomeView: React.FC<HomeViewProps> = ({
  scriptText,
  settings,
  canUndoClear,
  onScriptChange,
  onClearScript,
  onUndoClearScript,
  onUpdateSettings,
  onOpenSettings,
  onStartPrompter,
}) => {
  return (
    <div className="flex flex-col h-full w-full max-w-6xl mx-auto p-4 sm:p-6 select-none animate-fadeIn">
      {/* Top Header */}
      <header className="flex items-center justify-between pb-4 border-b border-neutral-800">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-white text-base shadow-md">
            TP
          </div>
          <div>
            <h1 className="text-base font-bold text-white tracking-wide">TP Arabic Teleprompter</h1>
            <p className="text-[11px] text-neutral-400">Broadcast-Grade • Local-First</p>
          </div>
        </div>

        <button
          onClick={onOpenSettings}
          className="flex items-center space-x-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 rounded-lg text-xs font-medium transition border border-neutral-700"
          aria-label="Open advanced settings"
        >
          <Sliders className="w-4 h-4" />
          <span>Advanced Settings</span>
        </button>
      </header>

      {/* Dominant Editor Viewport (§9) */}
      <main className="flex-1 py-4 min-h-0">
        <Editor
          text={scriptText}
          settings={settings}
          onChange={onScriptChange}
          onClear={onClearScript}
          onUndoClear={onUndoClearScript}
          canUndoClear={canUndoClear}
        />
      </main>

      {/* Compact Reading Setup Bar (§9) */}
      <footer className="bg-neutral-900 border border-neutral-800 rounded-xl p-3 sm:p-4 shadow-xl text-neutral-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Mode Selector: Manual vs Voice */}
          <div className="flex items-center space-x-1 bg-neutral-950 p-1 rounded-lg border border-neutral-800">
            <button
              onClick={() => onUpdateSettings({ modePreference: 'manual' })}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                settings.modePreference === 'manual'
                  ? 'bg-blue-600 text-white font-bold'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Manual</span>
            </button>
            <button
              onClick={() => onUpdateSettings({ modePreference: 'voice' })}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                settings.modePreference === 'voice'
                  ? 'bg-emerald-600 text-white font-bold'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Mic className="w-3.5 h-3.5" />
              <span>Voice Follow</span>
            </button>
          </div>

          {/* Quick Font Size Controls */}
          <div className="flex items-center space-x-2 text-xs">
            <span className="text-neutral-400">Font:</span>
            <div className="flex items-center space-x-1 bg-neutral-950 px-2 py-1 rounded-lg border border-neutral-800">
              <button
                onClick={() => onUpdateSettings({ fontSize: Math.max(24, settings.fontSize - 4) })}
                className="p-1 hover:bg-neutral-800 rounded text-neutral-300 hover:text-white"
                title="Decrease font size"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="font-mono text-white min-w-[2.5rem] text-center font-bold">
                {settings.fontSize}px
              </span>
              <button
                onClick={() => onUpdateSettings({ fontSize: Math.min(400, settings.fontSize + 4) })}
                className="p-1 hover:bg-neutral-800 rounded text-neutral-300 hover:text-white"
                title="Increase font size"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Quick Speed Controls */}
          <div className="flex items-center space-x-2 text-xs">
            <span className="text-neutral-400">Speed:</span>
            <div className="flex items-center space-x-1 bg-neutral-950 px-2 py-1 rounded-lg border border-neutral-800">
              <button
                onClick={() => onUpdateSettings({ speed: Math.max(2, Math.round(settings.speed * 0.96)) })}
                className="p-1 hover:bg-neutral-800 rounded text-neutral-300 hover:text-white"
                title="Decrease speed"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="font-mono text-white min-w-[3rem] text-center font-bold">
                {Math.round(settings.speed)}
              </span>
              <button
                onClick={() => onUpdateSettings({ speed: Math.min(1500, Math.round(settings.speed * 1.04)) })}
                className="p-1 hover:bg-neutral-800 rounded text-neutral-300 hover:text-white"
                title="Increase speed"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Countdown Selector */}
          <div className="flex items-center space-x-1 text-xs">
            <span className="text-neutral-400 mr-1">Countdown:</span>
            {[0, 3, 5, 10].map((sec) => (
              <button
                key={sec}
                onClick={() => onUpdateSettings({ countdownSeconds: sec })}
                className={`px-2 py-1 rounded text-xs transition ${
                  settings.countdownSeconds === sec
                    ? 'bg-neutral-700 text-white font-bold'
                    : 'text-neutral-400 hover:text-white'
                }`}
              >
                {sec === 0 ? 'Off' : `${sec}s`}
              </button>
            ))}
          </div>

          {/* START PROMPTING BUTTON */}
          <button
            onClick={onStartPrompter}
            disabled={!scriptText.trim()}
            className="flex items-center space-x-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-bold shadow-lg transition transform active:scale-95 disabled:opacity-30 disabled:pointer-events-none"
            aria-label="Start teleprompter"
          >
            <Play className="w-4 h-4 fill-current" />
            <span>Start Prompter</span>
          </button>
        </div>
      </footer>
    </div>
  );
};
